import styled from '@emotion/styled';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import type { SurveyListItem } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import { IconButton, DropdownMenu } from '@hotjar/hotsauce';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useExportResponses } from '../hooks/useExportResponses';
import { useDeleteSurvey } from '../hooks/useDeleteSurvey';

const StyledDropdownMenu = styled(DropdownMenu)`
  width: 220px;
`;

const getResultsRoute = (siteId: number, surveyId: number) => {
  return getRoute(SURVEYS.RESULTS.path, { siteId, surveyId });
};

const getEditRoute = (siteId: number, surveyId: number) => {
  return getRoute(SURVEYS.EDIT.path, { siteId, surveyId });
};

const getCopyRoute = (siteId: number, copyId: number) => {
  return getRoute(SURVEYS.CREATE.path, { siteId, copyId });
};

export interface SurveyActionsDropdownProps {
  survey: SurveyListItem;
  siteId: number;
}

export function SurveyActionsDropdown({
  survey,
  siteId,
}: SurveyActionsDropdownProps): JSX.Element | null {
  const { t } = useTranslation('feedback');
  const canUpdateSurvey = usePermission('surveys-update');
  const canCreateSurvey = usePermission('surveys-create');
  const canDeleteSurvey = usePermission('surveys-delete');

  const onViewResults = useCallback(() => {
    browserHistory.push(getResultsRoute(siteId, survey.id));
  }, [siteId, survey.id]);
  const onEdit = useCallback(() => {
    browserHistory.push(getEditRoute(siteId, survey.id));
  }, [siteId, survey.id]);
  const onDuplicate = useCallback(() => {
    browserHistory.push(getCopyRoute(siteId, survey.id));
  }, [siteId, survey.id]);

  const hasResponses = survey.response_count > 0;

  const { beginExport, fileTooLargeModal } = useExportResponses(survey.id);
  const beginExportCSV = useCallback(() => beginExport('csv'), [beginExport]);
  const beginExportXLSX = useCallback(() => beginExport('xlsx'), [beginExport]);

  const { beginDeleteSurvey, deleteSurveyModal } = useDeleteSurvey(survey.id, survey.name);

  return (
    <>
      <StyledDropdownMenu
        trigger={
          <IconButton
            icon="menu_kebab"
            tooltipText={t('survey-list.options')}
            description={t('survey-list.options')}
            kind="ghost"
            size="s"
          />
        }
      >
        {hasResponses ? (
          <DropdownMenu.Item icon="chart_bar" onClick={onViewResults}>
            {t('survey-list.action.results')}
          </DropdownMenu.Item>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
        {hasResponses ? (
          <DropdownMenu.Item icon="download" onClick={beginExportCSV}>
            {t('survey-list.action.download.csv')}
          </DropdownMenu.Item>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
        {hasResponses ? (
          <DropdownMenu.Item icon="download" onClick={beginExportXLSX}>
            {t('survey-list.action.download.xlsx')}
          </DropdownMenu.Item>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
        <DropdownMenu.Item
          icon="pencil"
          isDisabled={!canUpdateSurvey}
          onClick={onEdit}
          hasDivider={hasResponses}
        >
          {t('survey-list.action.edit')}
        </DropdownMenu.Item>
        <DropdownMenu.Item icon="duplicate" isDisabled={!canCreateSurvey} onClick={onDuplicate}>
          {t('survey-list.action.duplicate')}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          icon="trash"
          onClick={beginDeleteSurvey}
          isDisabled={!canDeleteSurvey}
          kind="danger_ghost"
          hasDivider
        >
          {t('survey-list.action.delete')}
        </DropdownMenu.Item>
      </StyledDropdownMenu>
      {fileTooLargeModal}
      {deleteSurveyModal}
    </>
  );
}
